import { useState, useMemo, useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { mockPartners } from '@/mocks/partners';
import { mockLeads } from '@/mocks/leads';
import CommandPalette from '@/components/CommandPalette';
import { VerticalOnboarding } from '@/components/VerticalOnboarding';
import { GlobalBackground } from '@/components/GlobalBackground';
import { IdleOverlay } from '@/components/IdleOverlay';
import { motion } from 'framer-motion';
import { SemmyActivityBar } from '@/components/SemmyActivityBar';
import { isAdmin } from '@/components/AdminOnly';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import {
  LayoutDashboard,
  Inbox,
  Users,
  FileText,
  BarChart3,
  Globe,
  Layers,
  Wallet,
  Settings,
  LogOut,
  Menu,
  X,
  ClipboardList,
  Activity,
  ChevronDown,
  Sparkles,
  Eye,
  Moon,
  Sun,
  Bot,
  Package,
  CalendarCheck,
  MessageCircle,
  MessageSquare,
  Megaphone,
} from 'lucide-react';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  roles: string[];
  badge?: number;
  divider?: boolean;
  adminOnly?: boolean;
  children?: { label: string; path: string }[];
}

const navItems: NavItem[] = [
  { label: 'Nova',      path: '/nova',      icon: <Bot size={16} strokeWidth={1.5} />,           roles: ['operator', 'read_only', 'account_manager'] },
  { label: 'Overview',  path: '/dashboard', icon: <LayoutDashboard size={16} strokeWidth={1.5} />, roles: ['operator', 'read_only'], divider: true },
  { label: 'Leads',     path: '/leads',     icon: <Inbox size={16} strokeWidth={1.5} />,          roles: ['operator', 'account_manager'], badge: mockLeads.filter(l => l.routing_status === 'unmatched' && !l.deleted_at).length },
  { label: 'Partners',  path: '/partners',  icon: <Users size={16} strokeWidth={1.5} />,          roles: ['operator', 'account_manager'] },
  { label: 'Offers',    path: '/offers',    icon: <FileText size={16} strokeWidth={1.5} />,       roles: ['operator'] },
  { label: 'Verticals', path: '/verticals', icon: <Globe size={16} strokeWidth={1.5} />,          roles: ['operator'], divider: true },
  {
    label: 'Finance',
    path: '/finance/payouts',
    icon: <Wallet size={16} strokeWidth={1.5} />,
    roles: ['operator'],
    children: [
      { label: 'Payouts',  path: '/finance/payouts' },
      { label: 'Invoices', path: '/finance/invoices' },
      { label: 'Disputes', path: '/finance/disputes' },
      { label: 'Summary',  path: '/finance/summary' },
    ],
  },
  { label: 'Reports',   path: '/reports',   icon: <BarChart3 size={16} strokeWidth={1.5} />,     roles: ['operator', 'read_only'] },
  { label: 'Capacity',  path: '/capacity',  icon: <Activity size={16} strokeWidth={1.5} />,      roles: ['operator'], divider: true },
  { label: 'Audit Log', path: '/audit-log', icon: <ClipboardList size={16} strokeWidth={1.5} />, roles: ['operator'] },
  { label: 'Settings',  path: '/settings',  icon: <Settings size={16} strokeWidth={1.5} />,      roles: ['operator'] },

  // Partner nav — Semmy (queue), Chat (nova), then ops, then analytics/billing, then settings
  { label: 'Semmy',       path: '/partner/semmy',       icon: <span style={{ fontWeight: 700, fontSize: '15px', letterSpacing: '-0.04em', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16 }}>s</span>, roles: ['partner_principal', 'partner_staff'] },
  { label: 'Chat',        path: '/partner/nova',        icon: <MessageSquare size={16} strokeWidth={1.5} />,   roles: ['partner_principal', 'partner_staff'], divider: true },
  { label: 'Leads',       path: '/partner/leads',       icon: <Layers size={16} strokeWidth={1.5} />,          roles: ['partner_principal', 'partner_staff'] },
  { label: 'Agenda',      path: '/partner/agenda',      icon: <CalendarCheck size={16} strokeWidth={1.5} />,   roles: ['partner_principal', 'partner_staff'] },
  { label: 'Advertenties', path: '/partner/ads',        icon: <Megaphone size={16} strokeWidth={1.5} />,       roles: ['partner_principal', 'partner_staff'], divider: true },
  { label: 'Catalogue',   path: '/partner/catalogue',   icon: <Package size={16} strokeWidth={1.5} />,         roles: ['partner_principal', 'partner_staff'], divider: true },
  { label: 'Performance', path: '/partner/performance', icon: <BarChart3 size={16} strokeWidth={1.5} />,       roles: ['partner_principal'] },
  { label: 'Billing',     path: '/partner/payouts',     icon: <Wallet size={16} strokeWidth={1.5} />,          roles: ['partner_principal'], divider: true },
  { label: 'Instellingen', path: '/partner/settings',  icon: <Settings size={16} strokeWidth={1.5} />,         roles: ['partner_principal', 'partner_staff'] },
  { label: 'Identiteit',  path: '/partner/semmy/identity', icon: <Sparkles size={16} strokeWidth={1.5} />, roles: ['partner_principal', 'partner_staff'], adminOnly: true },
];

const ROUTE_ORDER: Record<string, number> = {
  '/partner/semmy':       0,
  '/partner/nova':        1,
  '/partner/leads':       2,
  '/partner/agenda':      3,
  '/partner/ads':         4,
  '/partner/catalogue':   5,
  '/partner/performance': 6,
  '/partner/payouts':     7,
  '/partner/settings':    8,
};

const PARTNER_BOTTOM_TABS = [
  { label: 'Communicatie', path: '/partner/semmy',    icon: <span style={{ fontWeight: 700, fontSize: '18px', letterSpacing: '-0.04em', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20 }}>s</span> },
  { label: 'Chat',         path: '/partner/nova',     icon: <MessageSquare size={20} strokeWidth={1.5} /> },
  { label: 'Leads',        path: '/partner/leads',    icon: <Layers size={20} strokeWidth={1.5} /> },
  { label: 'Instellingen', path: '/partner/settings', icon: <Settings size={20} strokeWidth={1.5} /> },
];

export default function Layout() {
  const { user, signOut, previewRole, setPreviewRole, effectiveRole, effectivePartnerId, isLoading: authLoading } = useAuth();
  const { resolved, toggle } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [autonomousCount, setAutonomousCount] = useState(0);
  // blooming removed — no longer used
  const [displayPath, setDisplayPath] = useState(location.pathname);
  const [transitionCls, setTransitionCls] = useState<string>('page-enter-fwd');
  const prevPathnameRef = useRef(location.pathname);

  useEffect(() => {
    if (!userMenuOpen) return;
    const handleClick = () => setUserMenuOpen(false);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [userMenuOpen]);

  useEffect(() => {
    if (!effectiveRole?.startsWith('partner') || !effectivePartnerId) return;
    const flagKey = `onboarding_requested_${effectivePartnerId}`;
    if (localStorage.getItem(flagKey)) {
      localStorage.removeItem(flagKey);
      setShowOnboarding(true);
    }
  }, [effectiveRole, effectivePartnerId]);

  // Realtime autonomous action badge
  useEffect(() => {
    if (!effectivePartnerId || !isSupabaseConfigured) return;

    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const fetchCount = async () => {
      const { count } = await supabase
        .from('semmy_actions')
        .select('*', { count: 'exact', head: true })
        .eq('partner_id', effectivePartnerId)
        .eq('autonomous_send', true)
        .eq('status', 'sent')
        .gte('updated_at', todayStart.toISOString());
      setAutonomousCount(count ?? 0);
    };

    if (location.pathname === '/partner/semmy') {
      setAutonomousCount(0);
      return;
    }

    fetchCount();

    const channel = supabase
      .channel('semmy-autonomous-badge')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'semmy_actions', filter: `partner_id=eq.${effectivePartnerId}` },
        (payload: any) => {
          if (payload.new?.autonomous_send && payload.new?.status === 'sent') {
            setAutonomousCount((c: number) => c + 1);
          }
        },
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [effectivePartnerId, location.pathname]);

  // Page transition: enter-only directional slide — no exit animation to avoid tick/flash
  useEffect(() => {
    const prev = prevPathnameRef.current;
    const next = location.pathname;
    if (prev === next) return;

    const prevIdx = ROUTE_ORDER[prev] ?? -1;
    const nextIdx = ROUTE_ORDER[next] ?? -1;
    const fwd = nextIdx === -1 || prevIdx === -1 || nextIdx >= prevIdx;

    prevPathnameRef.current = next;
    setDisplayPath(next);

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!prefersReducedMotion) {
      setTransitionCls(fwd ? 'page-enter-fwd' : 'page-enter-back');
    } else {
      setTransitionCls('');
    }
  }, [location.pathname]);

  const partnerName = useMemo(() => {
    if (!user?.partner_id) return null;
    return mockPartners.find(p => p.id === user.partner_id)?.display_name || null;
  }, [user]);

  const filteredNav = useMemo(() => {
    if (!user) return [];
    return navItems.filter(item => {
      if (!item.roles.includes(effectiveRole ?? '')) return false;
      if (item.adminOnly && !isAdmin(user.email)) return false;
      return true;
    });
  }, [user, effectiveRole]);

  const isPartnerRole = effectiveRole === 'partner_principal' || effectiveRole === 'partner_staff';
  const isNovaPage = location.pathname === '/partner/nova';

  const toggleMenu = (label: string) => {
    setExpandedMenus(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const isActive = (path: string) => {
    const current = location.pathname;
    if (current === path) return true;
    const exactSibling = filteredNav.some(
      item =>
        item.path !== path &&
        (item.path === current || item.children?.some(c => c.path === current))
    );
    if (exactSibling) return false;
    return current.startsWith(path + '/');
  };

  if (authLoading) {
    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-surface)',
      }}>
        <div style={{
          width: '24px', height: '24px', borderRadius: '50%',
          border: '2px solid var(--accent)',
          borderTopColor: 'transparent',
          animation: 'spin 0.8s linear infinite',
        }} />
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] overflow-hidden" style={{ background: 'var(--bg-surface)' }}>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden animate-fade-in"
          style={{ background: 'var(--overlay-backdrop)', backdropFilter: 'blur(8px)' }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Sidebar ── */}
      <aside
        className={
          'fixed lg:static inset-y-0 left-0 z-50 flex flex-col overflow-hidden ' +
          'group/sidebar transition-all duration-300 ease-out ' +
          (sidebarOpen
            ? 'translate-x-0 w-[220px]'
            : '-translate-x-full lg:translate-x-0 lg:w-[52px] lg:hover:w-[200px]')
        }
        style={{
          background: resolved === 'light'
            ? 'rgba(255,255,255,0.78)'
            : 'rgba(10,10,10,0.55)',
          backdropFilter:       'blur(20px) saturate(160%)',
          WebkitBackdropFilter: 'blur(20px) saturate(160%)',
          borderRight: '1px solid var(--border-separator)',
          boxShadow: resolved === 'light' ? '2px 0 12px rgba(0,0,0,0.06)' : 'none',
        }}
      >
        {/* Logo */}
        <div className="h-[52px] flex items-center border-b border-[var(--border-separator)] shrink-0 relative overflow-hidden">
          <div className={
            'absolute inset-0 flex items-center justify-center transition-all duration-300 ' +
            (sidebarOpen
              ? 'opacity-0 scale-95 pointer-events-none'
              : 'opacity-0 scale-95 pointer-events-none lg:opacity-100 lg:scale-100 lg:pointer-events-auto lg:group-hover/sidebar:opacity-0 lg:group-hover/sidebar:scale-95 lg:group-hover/sidebar:pointer-events-none')
          }>
            <span
              className="text-[18px] font-semibold"
              style={{ fontFamily: "'StackSansNotch', sans-serif", color: 'var(--text-primary)' }}
            >
              C
            </span>
          </div>

          <div className={
            'w-full px-5 flex items-center transition-all duration-300 ease-out ' +
            (sidebarOpen
              ? 'opacity-100 translate-x-0'
              : 'opacity-0 -translate-x-2 pointer-events-none lg:group-hover/sidebar:opacity-100 lg:group-hover/sidebar:translate-x-0 lg:group-hover/sidebar:pointer-events-auto')
          }>
            <span
              className="text-[14px] font-semibold tracking-tight"
              style={{ fontFamily: "'StackSansNotch', sans-serif", color: 'var(--text-primary)' }}
            >
              converze
            </span>
          </div>

          <button
            aria-label="Menu sluiten"
            className="ml-auto lg:hidden p-1.5 rounded-md transition-colors z-10"
            style={{ color: 'var(--text-tertiary)' }}
            onClick={() => setSidebarOpen(false)}
          >
            <X size={16} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto pt-3 pb-3 px-1.5 flex flex-col gap-1">
          {filteredNav.map((item) => (
            <div key={item.path}>
              {item.children ? (
                <>
                  <button
                    onClick={() => toggleMenu(item.label)}
                    className={
                      'group w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-md text-[13px] transition-all duration-200 nav-link-hover ' +
                      (item.children.some(c => isActive(c.path)) ? 'font-medium nav-item-active' : '')
                    }
                  >
                    <div className="relative flex items-center justify-center w-5 shrink-0">
                      {item.icon}
                    </div>
                    <span className={
                      'overflow-hidden transition-all duration-300 ease-out whitespace-nowrap text-[13px] ' +
                      (sidebarOpen ? 'opacity-100 max-w-[140px]' : 'lg:opacity-0 lg:max-w-0 lg:group-hover/sidebar:opacity-100 lg:group-hover/sidebar:max-w-[140px]')
                    }>
                      {item.label}
                    </span>
                    <ChevronDown size={12} className={
                      'ml-auto shrink-0 transition-all duration-300 ' +
                      (expandedMenus[item.label] ? 'rotate-180' : '') + ' ' +
                      (sidebarOpen ? 'opacity-100' : 'lg:opacity-0 lg:group-hover/sidebar:opacity-100')
                    } style={{ color: 'var(--text-muted)' }} />
                  </button>

                  {expandedMenus[item.label] && (
                    <div className={
                      'mt-0.5 space-y-0.5 transition-all duration-200 ' +
                      (sidebarOpen ? 'ml-2' : 'lg:hidden lg:group-hover/sidebar:block lg:group-hover/sidebar:ml-2')
                    }>
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          onClick={() => setSidebarOpen(false)}
                          className={'block px-2.5 py-1.5 rounded-md text-[12px] transition-all duration-200 child-nav-link ' + (isActive(child.path) ? 'child-nav-link--active' : '')}
                          style={{ paddingLeft: sidebarOpen ? '40px' : '10px' }}
                        >
                          <span className={
                            'overflow-hidden transition-all duration-300 ease-out whitespace-nowrap ' +
                            (sidebarOpen ? 'opacity-100 max-w-[140px]' : 'lg:opacity-0 lg:max-w-0 lg:group-hover/sidebar:opacity-100 lg:group-hover/sidebar:max-w-[140px]')
                          }>
                            {child.label}
                          </span>
                        </Link>
                      ))}
                    </div>
                  )}
                  {item.divider && (
                    <hr className="my-1.5 mx-2" style={{ border: 'none', borderTop: '1px solid var(--border-separator)', opacity: 0.6 }} />
                  )}
                </>
              ) : (
                <Link
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={
                    'group relative flex items-center gap-2.5 px-2.5 py-2.5 rounded-md text-[13px] transition-all duration-200 nav-link-hover ' +
                    (isActive(item.path) ? 'font-medium nav-item-active nav-item-active--no-cssbar' : '')
                  }
                >
                  {isActive(item.path) && (
                    <motion.div
                      layoutId="partner-nav-active-bar"
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                      className="absolute left-0 rounded-r-full"
                      style={{
                        top: 0, bottom: 0, margin: 'auto 0',
                        width: '2.5px', height: '16px',
                        background: 'var(--accent)',
                      }}
                    />
                  )}
                  <div className="relative flex items-center justify-center w-5 shrink-0">
                    {item.icon}
                    {item.badge !== undefined && item.badge > 0 && (
                      <span
                        className={
                          'absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full animate-scale-in ' +
                          (sidebarOpen ? 'hidden' : 'lg:block hidden')
                        }
                        style={{ background: 'var(--danger-dot)', boxShadow: '0 0 0 1.5px var(--bg-sidebar)' }}
                      />
                    )}
                    {item.path === '/partner/semmy' && autonomousCount > 0 && (
                      <span
                        style={{
                          position:     'absolute',
                          top:          '-2px',
                          right:        '-2px',
                          width:        '7px',
                          height:       '7px',
                          borderRadius: '50%',
                          background:   'var(--accent)',
                          boxShadow:    '0 0 0 1.5px var(--bg-sidebar), 0 0 6px 1px var(--accent)',
                          animation:    'dot-breathe 2s ease-in-out infinite',
                        }}
                      />
                    )}
                  </div>
                  <span className={
                    'overflow-hidden transition-all duration-300 ease-out whitespace-nowrap text-[13px] ' +
                    (sidebarOpen ? 'opacity-100 max-w-[140px]' : 'lg:opacity-0 lg:max-w-0 lg:group-hover/sidebar:opacity-100 lg:group-hover/sidebar:max-w-[140px]')
                  }>
                    {item.label}
                  </span>
                  {item.badge !== undefined && item.badge > 0 && (
                    <span className={
                      'ml-auto text-[11px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center shrink-0 animate-scale-in ' +
                      (sidebarOpen ? 'block' : 'lg:hidden lg:group-hover/sidebar:block')
                    } style={{ background: 'var(--danger-text)', color: 'var(--text-inverse)' }}>
                      {item.badge}
                    </span>
                  )}
                  {!sidebarOpen && (
                    <div
                      className="absolute left-full ml-3 px-2.5 py-1.5 rounded-lg
                        whitespace-nowrap pointer-events-none z-50
                        opacity-0 group-hover:opacity-100
                        -translate-x-1 group-hover:translate-x-0
                        transition-all duration-150 hidden lg:block"
                      style={{
                        background: 'var(--bg-surface-raised)',
                        border:     '1px solid var(--border-primary)',
                        color:      'var(--text-secondary)',
                        fontSize:   '12px',
                        fontWeight: '500',
                        boxShadow:  '0 4px 12px rgba(0,0,0,0.15)',
                      }}
                    >
                      {item.label}
                    </div>
                  )}
                </Link>
              )}
              {item.divider && (
                <hr className="my-1.5 mx-2" style={{ border: 'none', borderTop: '1px solid var(--border-separator)', opacity: 0.6 }} />
              )}
            </div>
          ))}
        </nav>

        {/* Bottom actions */}
        <div className="p-1.5 border-t border-[var(--border-separator)] space-y-0.5 shrink-0">
          {/* Theme toggle */}
          <button
            onClick={toggle}
            title={resolved === 'dark' ? 'Licht thema' : 'Donker thema'}
            className="flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13px] w-full transition-all duration-200 hover-surface"
            style={{ color: 'var(--text-muted)' }}
          >
            <div className="flex items-center justify-center w-5 shrink-0">
              {resolved === 'dark' ? <Sun size={18} strokeWidth={1.5} /> : <Moon size={18} strokeWidth={1.5} />}
            </div>
            <span className={
              'overflow-hidden transition-all duration-300 ease-out whitespace-nowrap ' +
              (sidebarOpen ? 'opacity-100 max-w-[140px]' : 'lg:opacity-0 lg:max-w-0 lg:group-hover/sidebar:opacity-100 lg:group-hover/sidebar:max-w-[140px]')
            }>
              {resolved === 'dark' ? 'Licht thema' : 'Donker thema'}
            </span>
          </button>
          {/* Sign out */}
          <button
            onClick={signOut}
            title="Uitloggen"
            className="flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13px] w-full transition-all duration-200 btn-danger-hover"
          >
            <div className="flex items-center justify-center w-5 shrink-0">
              <LogOut size={18} strokeWidth={1.5} />
            </div>
            <span className={
              'overflow-hidden transition-all duration-300 ease-out whitespace-nowrap ' +
              (sidebarOpen ? 'opacity-100 max-w-[140px]' : 'lg:opacity-0 lg:max-w-0 lg:group-hover/sidebar:opacity-100 lg:group-hover/sidebar:max-w-[140px]')
            }>
              Uitloggen
            </span>
          </button>
        </div>
      </aside>

      {/* ── Main content ── */}
      <div
        className="flex-1 flex flex-col min-w-0 min-h-0 relative"
        style={{
          background: `
            radial-gradient(ellipse 80% 60% at -5% 0%,
              rgba(113,180,112,0.05) 0%, transparent 55%),
            radial-gradient(ellipse 50% 40% at 105% 100%,
              rgba(124,158,248,0.03) 0%, transparent 50%),
            var(--bg-surface)
          `,
        }}
      >
        <GlobalBackground />

        {!isPartnerRole && (<>
        {/* Top bar */}
        <header
          className="h-[52px] border-b border-[var(--border-primary)] flex items-center px-5 lg:px-8 gap-4 shrink-0 relative"
          style={{
            background: resolved === 'light'
              ? 'rgba(255,255,255,0.55)'
              : 'rgba(10,10,10,0.45)',
            backdropFilter:       'blur(20px) saturate(160%)',
            WebkitBackdropFilter: 'blur(20px) saturate(160%)',
            borderBottom: resolved === 'light'
              ? '1px solid rgba(0,0,0,0.04)'
              : '1px solid rgba(255,255,255,0.04)',
            zIndex: 1,
          }}
        >
          <button
            aria-label="Menu openen"
            className="lg:hidden p-2 rounded-lg transition-colors hover-surface"
            style={{ color: 'var(--text-tertiary)' }}
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={20} />
          </button>

          <span className="text-[11px] uppercase tracking-wider font-medium" style={{ color: 'var(--text-muted)' }}>
            {((): string => {
              const PAGE_LABELS: Record<string, string> = {
                '/':                    'Home',
                '/dashboard':           'Overview',
                '/partner/semmy':       'Communicatie',
                '/partner/nova':        'Chat',
                '/partner/leads':       'Leads',
                '/partner/agenda':      'Agenda',
                '/partner/settings':    'Instellingen',
                '/partner/ads':         'Advertenties',
                '/partner/performance': 'Resultaten',
                '/partner/payouts':     'Uitbetalingen',
                '/partner/catalogue':   'Catalogus',
              };
              return PAGE_LABELS[location.pathname] ?? location.pathname.split('/').filter(Boolean).pop() ?? 'Converze';
            })()}
          </span>

          <div className="ml-auto flex items-center gap-3">
            {/* Notification center — wire when ready */}

            <button
              onClick={(e) => { e.stopPropagation(); setUserMenuOpen(!userMenuOpen); }}
              className="flex items-center gap-2.5 px-3 py-1.5 rounded-full border transition-all duration-200 user-menu-btn"
              style={{ borderColor: 'var(--border-primary)' }}
              aria-label="Account menu"
            >
              <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: 'var(--accent-dim)', border: '1px solid var(--accent-glow)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 600, color: 'var(--accent)', flexShrink: 0 }}>
                {(user?.full_name || user?.email || '?')[0].toUpperCase()}
              </div>
              <span className="hidden sm:inline text-[11px] font-medium truncate max-w-[140px]" style={{ color: 'var(--text-tertiary)' }}>
                {user?.full_name || user?.email?.split('@')[0] || 'Account'}
              </span>
              <ChevronDown size={12} style={{ color: 'var(--text-muted)' }} className={`hidden sm:block transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </header>

        {/* User menu dropdown */}
        {userMenuOpen && (
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute top-[52px] right-5 lg:right-8 z-50 w-48 py-2 rounded-lg border shadow-lg animate-fade-in"
            style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
          >
            <button
              onClick={() => {
                signOut();
                setUserMenuOpen(false);
              }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] transition-colors btn-danger-hover"
            >
              <LogOut size={16} strokeWidth={1.5} />
              Logout
            </button>
          </div>
        )}
        </>)}

        {/* Floating hamburger for partner mobile (no top bar) */}
        {isPartnerRole && (
          <button
            aria-label="Menu openen"
            className="lg:hidden fixed top-3 left-3 p-2 rounded-lg transition-colors"
            style={{
              zIndex: 9200,
              color: 'var(--text-tertiary)',
              background: resolved === 'light' ? 'rgba(255,255,255,0.72)' : 'rgba(10,10,10,0.55)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid var(--border-separator)',
            }}
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={18} />
          </button>
        )}

        {/* Preview mode banner */}
        {previewRole && (
          <div
            className="shrink-0 px-5 py-2.5 border-b flex items-center justify-between animate-fade-in relative"
            style={{ background: 'var(--accent-dim)', borderColor: 'var(--accent-glow)', zIndex: 1 }}
          >
            <div className="flex items-center gap-2.5">
              <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-glow)' }}>
                <Sparkles size={10} style={{ color: 'var(--accent)' }} />
              </div>
              <span className="text-[13px] font-medium" style={{ color: 'var(--accent)' }}>
                Previewing as <span className="font-semibold">{previewRole.replace('_', ' ')}</span>
              </span>
              <span className="text-[11px] hidden sm:inline" style={{ color: 'var(--text-muted)' }}>
                Je beheertoegang is nog actief · schakel terug wanneer je wilt
              </span>
            </div>
            <button
              onClick={() => {
                setPreviewRole(null);
                navigate('/settings', { replace: true });
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-lg border transition-all"
              style={{ background: 'var(--bg-surface-raised)', color: 'var(--text-tertiary)', borderColor: 'var(--border-primary)' }}
            >
              <Eye size={13} />
              Return to Admin
            </button>
          </div>
        )}

        {/* Page content */}
        <main
          className={
            isNovaPage
              ? 'flex-1 overflow-hidden p-0 relative'
              : 'flex-1 overflow-y-auto overflow-x-hidden p-6 lg:p-8 relative ' + (isPartnerRole ? 'pb-[calc(76px+env(safe-area-inset-bottom))] lg:pb-8' : '')
          }
          style={{ zIndex: 1, scrollbarGutter: 'stable' }}
        >
          <div key={displayPath} className={transitionCls} style={isNovaPage ? { height: '100%' } : undefined}>
            <Outlet />
          </div>
        </main>
      </div>

      {/* ── Mobile bottom tab bar (partner only) ── */}
      {isPartnerRole && (
        <nav
          className="lg:hidden fixed bottom-0 left-0 right-0 z-50 flex items-center border-t"
          style={{
            background: resolved === 'light'
              ? 'rgba(255,255,255,0.78)'
              : 'rgba(10,10,10,0.55)',
            borderColor:           'var(--border-separator)',
            backdropFilter:        'blur(20px) saturate(160%)',
            WebkitBackdropFilter:  'blur(20px) saturate(160%)',
            paddingBottom:         'env(safe-area-inset-bottom)',
            minHeight:             '60px',
          }}
        >
          {PARTNER_BOTTOM_TABS.map(tab => {
            const active = isActive(tab.path);
            return (
              <Link
                key={tab.path}
                to={tab.path}
                className={'flex-1 flex flex-col items-center justify-center gap-0.5 py-2 bottom-tab-item ' + (active ? 'is-active' : '')}
              >
                {tab.icon}
                <span className="text-[11px] font-medium truncate max-w-[56px] text-center">{tab.label}</span>
              </Link>
            );
          })}
        </nav>
      )}

      {showOnboarding && effectivePartnerId && (
        <VerticalOnboarding
          partnerId={effectivePartnerId}
          onComplete={() => setShowOnboarding(false)}
        />
      )}

      {isPartnerRole && (
        <SemmyActivityBar
          partnerId={effectivePartnerId}
          bottomOffset={typeof window !== 'undefined' && window.innerWidth < 1024 ? 76 : 24}
          dimmed={sidebarOpen}
        />
      )}

      <CommandPalette />

      <IdleOverlay disabled={showOnboarding} />

    </div>
  );
}
